import { useCallback, useState, type ReactElement } from 'react';
import { useRouter } from 'next/router';
import { confirmAlert } from 'react-confirm-alert';

// Design System
import { Font } from '@utmb/design-system/text/font';
import { Image } from '@utmb/design-system/image/Image';
import { Placeholder } from '@utmb/design-system/placeholder/Placeholder';
import { Flag } from '@utmb/design-system/flag/Flag';
// Contexts
import { useUser } from '@utmb/contexts/AuthContext';
import { useTenancy } from '@utmb/contexts/TenancyContext';
// Types
import RunnerView from '@utmb/types/view/RunnerView';
import UserMemberAttachView from '@utmb/types/view/UserMemberAttachView';
import { UserMemberAttachStatusEnum } from '@utmb/types/enum/UserMemberAttachStatusEnum';
import { ColorTypesEnum } from '@utmb/types/colors';
// Utils
import { getColorStyle } from '@utmb/utils/colors';
import { pIdToCloudinary } from '@utmb/utils/cloudinary';
import { FetchMethodsEnum, fetchTheAPI } from '@utmb/utils/utmb';
// Styles
import 'react-confirm-alert/src/react-confirm-alert.css';
import styles from './runner-header-remake.module.scss';
import { SocialMediaTypeEnum } from '@utmb/types/enum/SocialMediaTypeEnum';
import { IconEnum } from '@utmb/types/enum/IconEnum';
import { Link } from '@utmb/design-system/link/link';
import { Icon } from '@utmb/design-system/icons/Icon';
import { IconsEnum } from '@utmb/types/icons';
import { useTranslation } from 'next-i18next';
import { getGender } from '@utmb/utils/gender';

export type RunnerHeaderRemakeProps = RunnerView;

const DESCRIPTION_MAX_LENGTH = 175;

export function RunnerHeaderRemake(props: RunnerHeaderRemakeProps): ReactElement {
    const { t } = useTranslation();
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
    const [isPendingClaimPage, setIsPendingClaimPage] = useState(false);
    const [claimIsSuccess, setClaimIsSuccess] = useState(false);

    const user = useUser();
    const { tenantId } = useTenancy();
    const router = useRouter();

    const {
        profilePicture,
        bannerPicture,
        fullname,
        nationalityCode,
        ageGroup,
        gender,
        club,
        team,
        sponsor,
        description,
        socials,
        website,
        nationality,
        alreadyAskedAttach,
        isLinkedToUser,
    } = props;

    const runnerParam = router.query.runner;

    const isDescriptionTruncated = !!description && description.length > DESCRIPTION_MAX_LENGTH;
    const displayedDescription = isDescriptionTruncated && !isDescriptionExpanded ? description.slice(0, DESCRIPTION_MAX_LENGTH) : description;

    const doRequestToClaimPage = useCallback(async () => {
        setIsPendingClaimPage(true);

        const runnerId = parseInt(runnerParam as string);
        const results = (await fetchTheAPI({
            tenantId,
            path: `/users/me/attach/${runnerId}`,
            bearer: await user.getToken(),
            method: FetchMethodsEnum.POST,
        })) as UserMemberAttachView;

        const acceptedRequests = [
            UserMemberAttachStatusEnum.OK_ALREADY_REQUESTED,
            UserMemberAttachStatusEnum.OK_RUNNER_ASSOCIATED,
            UserMemberAttachStatusEnum.OK_RUNNER_ASSOCIATION_REQUESTED,
        ];

        if (acceptedRequests.includes(results.status)) {
            setClaimIsSuccess(true);
        } else {
            setClaimIsSuccess(false);
            setIsPendingClaimPage(false);

            confirmAlert({
                title: t('singleRunner.claimError.title'),
                message: t('singleRunner.claimError.message'),
                closeOnClickOutside: true,
                closeOnEscape: true,
                buttons: [
                    {
                        label: t('common.close'),
                        onClick: () => null,
                    },
                ],
            });
        }
    }, [user, runnerParam, tenantId, t]);

    const handleClaimPageButtonClick = useCallback(
        async (event) => {
            event.preventDefault();

            if (isPendingClaimPage) {
                return;
            }

            if (!user.isAuthenticated) {
                user.login();

                return;
            }

            confirmAlert({
                title: t('singleRunner.confirm.title'),
                message: t('singleRunner.confirm.message'),
                closeOnClickOutside: true,
                closeOnEscape: true,
                buttons: [
                    {
                        label: t('common.confirm'),
                        onClick: doRequestToClaimPage,
                    },
                    {
                        label: t('common.cancel'),
                        onClick: () => null,
                    },
                ],
            });
        },
        [isPendingClaimPage, user, t, doRequestToClaimPage]
    );

    return (
        <div className={styles.runnerHeader}>
            <div className={styles.content}>
                <div className={styles.avatar}>
                    {profilePicture ? (
                        <Image objectFit="cover" layout="fill" image={pIdToCloudinary(profilePicture, fullname)} />
                    ) : (
                        <Placeholder className={styles.placeholder} isInvert logoWidth={60} logoHeight={60} />
                    )}
                </div>
                {!isLinkedToUser && !alreadyAskedAttach && !claimIsSuccess && (
                    <Link href="#" onClick={handleClaimPageButtonClick}>
                        <Font.FuturaBook desktop="16" mobile="14">
                            {t(`singleRunner.claimPageButtonLabel.${user.isAuthenticated ? 'logged' : 'notLogged'}`)}
                        </Font.FuturaBook>
                    </Link>
                )}
                {((!isLinkedToUser && alreadyAskedAttach) || claimIsSuccess) && (
                    <Font.FuturaBook desktop="20" mobile="20" className={styles.claimPagePending}>
                        {t('singleRunner.pendingLabel')}
                    </Font.FuturaBook>
                )}
                <Font.FuturaMedium as="h1" className={styles.name} desktop="32" mobile="32">
                    {fullname}
                </Font.FuturaMedium>
                <div className={styles.countryCategory}>
                    <div className={styles.country}>
                        {nationalityCode && <Flag className={styles.flag} isoCode={nationalityCode.toLowerCase()} hideShadow />}
                        <Font.FuturaMedium desktop="16" mobile="16" className={styles.category} color={getColorStyle(ColorTypesEnum.GREY_TEXT)}>
                            {nationality}
                        </Font.FuturaMedium>
                    </div>
                    <div className={styles.divider} />
                    {ageGroup && (
                        <Font.FuturaMedium desktop="16" mobile="16" className={styles.category} color={getColorStyle(ColorTypesEnum.GREY_TEXT)}>
                            {ageGroup} {t(getGender(gender))}
                        </Font.FuturaMedium>
                    )}
                </div>
                {description && (
                    <Font.FuturaBook className={styles.decription} desktop="16" mobile="16">
                        {displayedDescription}
                        {isDescriptionTruncated && (
                            <>
                                {!isDescriptionExpanded ? '... ' : ' '}
                                <span
                                    role="button"
                                    tabIndex={0}
                                    className={styles.readMore}
                                    onClick={() => setIsDescriptionExpanded((v) => !v)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setIsDescriptionExpanded((v) => !v);
                                        }
                                    }}
                                >
                                    {isDescriptionExpanded
                                        ? t('singleRunner.runnerHeader.description.seeLess')
                                        : t('singleRunner.runnerHeader.description.seeMore')}
                                </span>
                            </>
                        )}
                    </Font.FuturaBook>
                )}
                <div className={styles.infos}>
                    {club && (
                        <div className={styles.info}>
                            <Font.FuturaBook className={styles.label} desktop="16" mobile="16" color={getColorStyle(ColorTypesEnum.GREY_TEXT)}>
                                {t('singleRunner.runnerHeader.club')}
                            </Font.FuturaBook>
                            <Font.FuturaMedium className={styles.value} desktop="16" mobile="16">
                                {club}
                            </Font.FuturaMedium>
                        </div>
                    )}
                    {team && (
                        <div className={styles.info}>
                            <Font.FuturaBook className={styles.label} desktop="16" mobile="16" color={getColorStyle(ColorTypesEnum.GREY_TEXT)}>
                                {t('singleRunner.runnerHeader.team')}
                            </Font.FuturaBook>
                            <Font.FuturaMedium className={styles.value} desktop="16" mobile="16">
                                {team}
                            </Font.FuturaMedium>
                        </div>
                    )}
                    {sponsor && (
                        <div className={styles.info}>
                            <Font.FuturaBook className={styles.label} desktop="16" mobile="16" color={getColorStyle(ColorTypesEnum.GREY_TEXT)}>
                                {t('singleRunner.runnerHeader.sponsor')}
                            </Font.FuturaBook>
                            <Font.FuturaMedium className={styles.value} desktop="16" mobile="16">
                                {sponsor}
                            </Font.FuturaMedium>
                        </div>
                    )}
                </div>
                {((socials && socials.length) > 0 || website) && (
                    <div className={styles.wrapSocials}>
                        <Font.FuturaBook className={styles.label} desktop="16" mobile="16" color={getColorStyle(ColorTypesEnum.GREY_TEXT)}>
                            {t('singleRunner.runnerHeader.socials')}
                        </Font.FuturaBook>
                        <div className={styles.socials}>
                            {website ? (
                                <Link href={website} target="_blank" className={styles.social + ' ' + styles.website}>
                                    <Icon variant={IconsEnum.CHAIN} />
                                </Link>
                            ) : null}
                            {socials.map((social, index) => {
                                let iconVariant = social.type as unknown as IconEnum;
                                if (social.type === SocialMediaTypeEnum.X) iconVariant = IconEnum.SOCIAL_X;
                                return (
                                    <Link href={social.url} target="_blank" className={styles.social} key={`social${index}`}>
                                        <Icon variant={iconVariant} />
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                )}
                <div className={styles.social}></div>
            </div>
            <div className={styles.banner}>
                {bannerPicture ? (
                    <Image objectFit="cover" layout="fill" image={pIdToCloudinary(bannerPicture, fullname)} />
                ) : (
                    <Placeholder className={styles.placeholder} logoWidth={60} logoHeight={60} />
                )}
            </div>
        </div>
    );
}
